/**
 * Instrument v2: the single source of design values for the add-in, ported from the web app's
 * `app/styles/tokens/tokens.ts`. `DESIGN.shared.md` describes them. `cssVars.ts` emits every
 * group as `--j-*` custom properties; components read those variables.
 */ export const ROOT_PX = 16;
export const fontFamily = "'Inter', 'Inter Variable', system-ui, -apple-system, 'Segoe UI', sans-serif";
export const colour = {
    surface: "#ffffff",
    panel: "#f7f8f9",
    well: "#eceef1",
    hover: "#f3f4f6",
    lineSoft: "#eceef1",
    line: "#e1e4e8",
    lineStrong: "#cfd4da",
    lineCb: "#bcc2c9",
    ink: "#1a1d21",
    ink2: "#59606a",
    ink3: "#6b727c",
    onAccent: "#ffffff",
    accent: "#546867",
    accentHover: "#3c504f",
    accentSoft: "#e5eeec",
    accentInk: "#3c504f",
    ok: "#2f6f4e",
    warn: "#8f5f0e",
    err: "#a63a2e",
    info: "#3d6182",
    infoInk: "#3d6182",
    wait: "#7a5e55",
    idle: "#646b6c",
    shadow: "#1a1d21"
};
/** Word's dark mode. Accent fills turn light, so `onAccent` turns dark to keep their labels readable. */ export const darkColour = {
    surface: "#1b1d20",
    panel: "#202226",
    well: "#2a2d31",
    hover: "#25282c",
    lineSoft: "#26292d",
    line: "#2f3237",
    lineStrong: "#3d4147",
    lineCb: "#6b717a",
    ink: "#e5e7ea",
    ink2: "#a7adb5",
    ink3: "#959ba4",
    onAccent: "#101615",
    accent: "#93aba8",
    accentHover: "#a9bfbc",
    accentSoft: "#26302f",
    accentInk: "#a9c0bd",
    ok: "#6cc08f",
    warn: "#dca956",
    err: "#f08c7c",
    info: "#8db3da",
    infoInk: "#9dbde0",
    wait: "#c09f94",
    idle: "#979e9f",
    shadow: "#000000"
};
export const tags = {
    grey: {
        bg: "#eceef1",
        fg: "#454c55"
    },
    blue: {
        bg: "#e3ecf7",
        fg: "#284b73"
    },
    violet: {
        bg: "#ece7f6",
        fg: "#4f3d7a"
    },
    rose: {
        bg: "#f6e6ec",
        fg: "#7a2e4a"
    },
    orange: {
        bg: "#fbe7da",
        fg: "#8a3f12"
    },
    amber: {
        bg: "#f8edd5",
        fg: "#6e4c0c"
    },
    green: {
        bg: "#e4f0e2",
        fg: "#2e5a2b"
    },
    olive: {
        bg: "#eef0d9",
        fg: "#4f5419"
    }
};
export const darkTags = {
    grey: {
        bg: "#2b2e33",
        fg: "#c4c9d0"
    },
    blue: {
        bg: "#1d2a3a",
        fg: "#a9c6ea"
    },
    violet: {
        bg: "#28233a",
        fg: "#c8baea"
    },
    rose: {
        bg: "#37222c",
        fg: "#ebb6c9"
    },
    orange: {
        bg: "#3a2518",
        fg: "#f0b48d"
    },
    amber: {
        bg: "#342812",
        fg: "#e6c47e"
    },
    green: {
        bg: "#1d2d1d",
        fg: "#a9d4a4"
    },
    olive: {
        bg: "#2b2d16",
        fg: "#d0d48e"
    }
};
export const type = {
    "2xs": {
        size: "0.625rem",
        lineHeight: "0.75rem"
    },
    xs: {
        size: "0.6875rem",
        lineHeight: "1rem"
    },
    sm: {
        size: "0.75rem",
        lineHeight: "1rem"
    },
    md: {
        size: "0.8125rem",
        lineHeight: "1.125rem"
    },
    lg: {
        size: "0.875rem",
        lineHeight: "1.25rem"
    },
    xl: {
        size: "1rem",
        lineHeight: "1.375rem"
    },
    "2xl": {
        size: "1.25rem",
        lineHeight: "1.75rem"
    }
};
export const titleTracking = "-0.006em";
export const space = {
    "0-5": "0.125rem",
    "1": "0.25rem",
    "1-5": "0.375rem",
    "2": "0.5rem",
    "3": "0.75rem",
    "4": "1rem",
    "6": "1.5rem",
    "8": "2rem"
};
export const radius = {
    xs: "0.125rem",
    sm: "0.25rem",
    md: "0.375rem",
    lg: "0.5rem"
};
export const size = {
    hdr: "2.5rem",
    bar: "2.25rem",
    btnH: "1.75rem",
    btnHLg: "2rem",
    btnPx: "0.625rem",
    btnPxIcon: "0.5rem",
    btnGap: "0.375rem",
    tagH: "1.25rem",
    av: "1.375rem",
    icon: "1rem"
};
/** The add-in is always compact. */ export const density = {
    row: "1.75rem",
    th: "1.75rem",
    cellX: "0.5rem"
};
/** Flat: only floating layers take a shadow. e1 tooltips, e2 menus and popovers, e3 dialogs and toasts. */ export const elevation = {
    e0: "none",
    e1: "0 0 0 1px var(--j-line), 0 0.0625rem 0.125rem rgb(var(--j-shadow-rgb) / 0.04)",
    e2: "0 0 0 1px var(--j-line), 0 0.125rem 0.5rem rgb(var(--j-shadow-rgb) / 0.06)",
    e3: "0 0 0 1px var(--j-line), 0 0.25rem 1rem rgb(var(--j-shadow-rgb) / 0.08)"
};
export const focus = {
    w: "2px",
    offset: "1px",
    insetOffset: "-2px"
};
export const motion = {
    ease: "cubic-bezier(0.16, 1, 0.3, 1)",
    durFade: "200ms",
    durSize: "250ms"
};
